import React, { useEffect, useMemo, useState } from 'react';
import { accent, darkPurple } from '../utils/colors';
import { fetchGrades, createGrade, updateGrade, deleteGrade } from '../utils/api';

const emptyForm = { id: 0, title: '', orderNum: 0, departmentId: 0 };

const GradesPage = ({ apiBase }) => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const list = await fetchGrades(apiBase, { search, take: 500, page: 1 });
      const normalized = (list || []).map(g => ({
        id: g.id ?? g.Id,
        code: g.code ?? g.Code,
        title: g.title ?? g.Title,
        orderNum: g.orderNum ?? g.OrderNum ?? 0,
        departmentId: g.departmentId ?? g.DepartmentId ?? 0,
      })).sort((a, b) => (a.orderNum - b.orderNum) || a.title.localeCompare(b.title, 'ru'));
      setGrades(normalized);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (apiBase) load(); }, [apiBase]);

  const startCreate = () => {
    setEditing({ ...emptyForm });
    setForm({ ...emptyForm });
  };

  const startEdit = (g) => {
    const model = { id: g.id, title: g.title || '', orderNum: g.orderNum || 0, departmentId: g.departmentId || 0 };
    setEditing(model);
    setForm(model);
  };

  const cancel = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.title) return;
    setLoading(true);
    setError('');
    try {
      const payload = { id: form.id || 0, title: form.title.trim(), orderNum: Number(form.orderNum) || 0, departmentId: Number(form.departmentId) || 0 };
      if (payload.id && payload.id > 0) {
        await updateGrade(apiBase, payload);
      } else {
        const res = await createGrade(apiBase, payload);
        const newId = res.payload;
        payload.id = newId ?? payload.id;
      }
      await load();
      cancel();
    } catch (e) {
      setError(e.message || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Удалить грейд?')) return;
    setLoading(true);
    setError('');
    try {
      await deleteGrade(apiBase, id);
      await load();
    } catch (e) {
      setError(e.message || 'Ошибка удаления');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 4px 24px #a259f733' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ color: accent, fontWeight: 700, fontSize: 20 }}>Грейды</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск..."
            style={{ border: `1.5px solid ${accent}`, borderRadius: 8, padding: '6px 10px', outline: 'none' }}
          />
          <button onClick={load} style={{ background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>Найти</button>
          <button onClick={startCreate} style={{ background: '#eee', color: accent, border: 'none', borderRadius: 6, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>Добавить</button>
        </div>
      </div>

      {editing && (
        <div style={{ background: '#f7f3ff', borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <div style={{ maxWidth: 400, margin: '0 auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Название" style={{ border: `1.5px solid ${accent}`, borderRadius: 8, padding: '6px 10px', width: '100%' }} />
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#333' }}>Порядок</label>
                <input type="number" value={form.orderNum} onChange={e => setForm(f => ({ ...f, orderNum: Number(e.target.value) }))} style={{ width: '100%', padding: '8px 10px', border: `1.5px solid ${accent}`, borderRadius: 8 }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#333' }}>Id департамента</label>
                <input type="number" value={form.departmentId} onChange={e => setForm(f => ({ ...f, departmentId: Number(e.target.value) }))} style={{ width: '100%', padding: '8px 10px', border: `1.5px solid ${accent}`, borderRadius: 8 }} />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <button onClick={save} disabled={!form.title || loading} style={{ background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 16, fontWeight: 600, cursor: 'pointer', marginRight: 10 }}>{loading ? 'Сохраняю...' : 'Сохранить'}</button>
              <button onClick={cancel} disabled={loading} style={{ background: '#eee', color: accent, border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>Отмена</button>
              {error && <span style={{ color: 'red', marginLeft: 10 }}>{error}</span>}
            </div>
          </div>
        </div>
      )}

      {loading && <div style={{ color: darkPurple }}>Загрузка...</div>}
      {error && !editing && <div style={{ color: 'red' }}>{error}</div>}

      {!loading && (
        <div style={{ overflow: 'auto', maxHeight: 520, border: '1px solid #eee', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ background: '#faf7ff', color: darkPurple, padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0e9ff' }}>Название</th>
                <th style={{ background: '#faf7ff', color: darkPurple, padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f0e9ff' }}>Порядок</th>
                <th style={{ background: '#faf7ff', color: darkPurple, padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f0e9ff' }}>Id департамента</th>
                <th style={{ background: '#faf7ff', color: darkPurple, padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #f0e9ff', width: 160 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {grades.map(g => (
                <tr key={g.id}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f7f3ff' }}>{g.title}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f7f3ff', textAlign: 'right' }}>{g.orderNum}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f7f3ff', textAlign: 'right' }}>{g.departmentId}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f7f3ff', textAlign: 'center' }}>
                    <button onClick={() => startEdit(g)} style={{ background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', marginRight: 8 }}>Редактировать</button>
                    <button onClick={() => remove(g.id)} style={{ background: '#eee', color: '#d00', border: 'none', borderRadius: 6, padding: '6px 12px', fontWeight: 700, cursor: 'pointer' }}>Удалить</button>
                  </td>
                </tr>
              ))}
              {grades.length === 0 && (
                <tr><td colSpan={3} style={{ padding: 16, color: '#888', textAlign: 'center' }}>Пусто</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GradesPage;

